import React, { useState, useEffect, useRef } from 'react';
import { Mail, X, Maximize2, Pencil, Check } from 'lucide-react';
import { brand } from '../../config/brand';

const TAB_WIDTH = 280;
const TAB_GAP = 8;

// Couleur de fond de l'onglet selon le tenant (déjà défini dans brand.js)
const TAB_BG = brand.colors.primary;
const TAB_BG_HOVER = brand.colors.primaryHover || brand.colors.primary;

/**
 * MinimizedMailTab — Onglet draggable pour une fenêtre de composition minimisée.
 */
const MinimizedMailTab = ({ win, index, onRestore, onClose, onPositionChange, onRename }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const tabRef = useRef(null);
  const dragStartPos = useRef(null);
  const hasMoved = useRef(false);

  // Titre affiché : nom personnalisé → sujet → destinataire → défaut
  const displayTitle = win.customTitle || win.form?.subject || win.form?.to || 'Nouveau message';

  // Édition inline du nom
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const inputRef = useRef(null);

  const startEditing = () => {
    setEditValue(win.customTitle || win.form?.subject || win.form?.to || '');
    setEditing(true);
  };

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const commitEditing = () => {
    onRename?.(editValue.trim());
    setEditing(false);
  };

  // Position par défaut : empilé en bas à gauche. Position fixe si déplacé.
  const tabStyle = win.tabPosition?.x != null
    ? { position: 'fixed', left: win.tabPosition.x, top: win.tabPosition.y, zIndex: 1000, width: TAB_WIDTH }
    : { position: 'fixed', left: 4 + index * (TAB_WIDTH + TAB_GAP), bottom: 0, zIndex: 1000, width: TAB_WIDTH };

  const handleMouseDown = (e) => {
    if (e.target.closest('button')) return;
    e.preventDefault();
    const rect = tabRef.current.getBoundingClientRect();
    dragStartPos.current = { x: e.clientX, y: e.clientY };
    hasMoved.current = false;
    setDragOffset({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    setIsDragging(true);
  };

  const handleTitleClick = () => {
    // Ne restaurer que si l'onglet n'a pas été déplacé
    if (!hasMoved.current) onRestore();
  };

  useEffect(() => {
    if (!isDragging) return;
    const handleMouseMove = (e) => {
      const dx = e.clientX - (dragStartPos.current?.x ?? e.clientX);
      const dy = e.clientY - (dragStartPos.current?.y ?? e.clientY);
      if (Math.abs(dx) > 5 || Math.abs(dy) > 5) hasMoved.current = true;

      const newX = Math.max(0, Math.min(e.clientX - dragOffset.x, window.innerWidth - TAB_WIDTH));
      const newY = Math.max(0, Math.min(e.clientY - dragOffset.y, window.innerHeight - 44));
      onPositionChange({ x: newX, y: newY });
    };
    const handleMouseUp = () => setIsDragging(false);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset, onPositionChange]);

  return (
    <div
      ref={tabRef}
      style={{ ...tabStyle, backgroundColor: TAB_BG }}
      className={`rounded-t-xl shadow-2xl flex items-center select-none ${
        isDragging ? 'cursor-grabbing' : 'cursor-grab'
      }`}
      onMouseDown={handleMouseDown}
    >
      {/* Titre cliquable (ou champ d'édition) */}
      <div
        className="flex-1 flex items-center space-x-2 px-3 py-3 min-w-0"
        onMouseDown={(e) => { if (editing) e.stopPropagation(); }}
      >
        <Mail size={15} className="text-white shrink-0 opacity-80" />
        {editing ? (
          <input
            ref={inputRef}
            type="text"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitEditing();
              if (e.key === 'Escape') setEditing(false);
            }}
            onBlur={commitEditing}
            onClick={(e) => e.stopPropagation()}
            placeholder="Nom du brouillon…"
            data-testid={`minimized-mail-rename-input-${win.id}`}
            className="flex-1 min-w-0 bg-white/95 text-slate-800 text-sm rounded px-2 py-0.5 outline-none focus:ring-2 focus:ring-white"
          />
        ) : (
          <span
            className="text-sm font-medium text-white truncate cursor-pointer"
            onClick={handleTitleClick}
            onDoubleClick={(e) => { e.stopPropagation(); startEditing(); }}
            title="Cliquer pour restaurer · Double-cliquer pour renommer"
            data-testid={`minimized-mail-title-${win.id}`}
          >
            {displayTitle}
          </span>
        )}
      </div>

      {/* Boutons */}
      <div className="flex items-center pr-2 shrink-0">
        {editing ? (
          <button
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); commitEditing(); }}
            className="p-1.5 text-white/70 hover:text-white hover:bg-white/20 rounded transition-colors"
            title="Valider le nom"
            data-testid={`minimized-mail-rename-save-${win.id}`}
          >
            <Check size={14} />
          </button>
        ) : (
          <button
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); startEditing(); }}
            className="p-1.5 text-white/70 hover:text-white hover:bg-white/20 rounded transition-colors"
            title="Renommer"
            data-testid={`minimized-mail-rename-${win.id}`}
          >
            <Pencil size={14} />
          </button>
        )}
        <button
          onMouseDown={(e) => e.stopPropagation()}
          onClick={onRestore}
          className="p-1.5 text-white/70 hover:text-white hover:bg-white/20 rounded transition-colors"
          title="Restaurer"
        >
          <Maximize2 size={14} />
        </button>
        <button
          onMouseDown={(e) => e.stopPropagation()}
          onClick={onClose}
          className="p-1.5 text-white/70 hover:text-white hover:bg-white/20 rounded transition-colors"
          title="Fermer"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
};

/**
 * MinimizedMailTabs — Rend tous les onglets de fenêtres minimisées.
 */
const MinimizedMailTabs = ({ minimizedWindows, onRestore, onClose, onPositionChange, onRename }) => {
  if (!minimizedWindows || minimizedWindows.length === 0) return null;
  return (
    <>
      {minimizedWindows.map((win, index) => (
        <MinimizedMailTab
          key={win.id}
          win={win}
          index={index}
          onRestore={() => onRestore(win.id)}
          onClose={() => onClose(win.id)}
          onPositionChange={(pos) => onPositionChange(win.id, pos)}
          onRename={(title) => onRename?.(win.id, title)}
        />
      ))}
    </>
  );
};

export default MinimizedMailTabs;
